/**
 * Why the host is looking at the login page.
 *
 * PrivateRoute sends two very different people to /login: someone who was
 * never signed in and clicked a protected link, and a host who WAS working and
 * whose session ended underneath them. Both saw the same marketing headline —
 * "Welcome back. Log in to your account to continue" — with no mention that
 * anything had happened. That is how an ordinary, expected sign-out reads as
 * "Canira logged me out for no reason", which is the exact wording a host used
 * when the session lifetimes were still 24 hours.
 *
 * The API client already knows which it is (utils/sessionEvents emits a reason)
 * and the knowledge was being thrown away at the navigation. This carries it
 * across, and only across: the notice is READ ONCE AND CLEARED, so a later
 * visit to /login — or a refresh of it — does not keep announcing a sign-out
 * that happened days ago.
 *
 * sessionStorage rather than a module variable because the teardown may be
 * followed by a full page load, which would discard module state; and rather
 * than localStorage because this is per-tab and must not outlive the tab.
 *
 * Every access is wrapped. Safari in private mode makes the accessor itself
 * throw, and the whole value of this is one explanatory line — losing it must
 * never cost the host the login page.
 */

const KEY = 'canira_session_ended';

/** Reasons utils/sessionEvents can emit, and what each one means to a person. */
const COPY = {
  // A 401 carrying SESSION_REVOKED: signed out elsewhere, password changed,
  // admin action. Naming the likely causes stops it reading as a fault.
  revoked: 'You were signed out of Canira. That happens if you changed your password, signed out on another device, or someone signed out of all sessions.',
  // The refresh endpoint refused this session: it timed out, or the 30-day
  // refresh credential expired. Deliberately not worded as an error.
  refresh_failed: 'Your session has ended, so you will need to sign in again.',
};

const FALLBACK = 'Your session has ended, so you will need to sign in again.';

/**
 * Record that a session just ended. Called from the one place that knows —
 * AuthContext's session-lost subscriber.
 */
export function noteSessionEnded(reason) {
  try {
    window.sessionStorage.setItem(KEY, String(reason || ''));
  } catch {
    /* no notice is better than no login page */
  }
}

/**
 * Read and clear the notice. Returns null when the visitor simply wasn't
 * signed in — which is most people who land on /login, and who must not be
 * told their session ended.
 */
export function takeSessionEndedNotice() {
  let reason = null;
  try {
    reason = window.sessionStorage.getItem(KEY);
    if (reason !== null) window.sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (reason === null) return null;
  return { reason, message: COPY[reason] || FALLBACK };
}

/** Exported for the tests that pin the copy. */
export const SESSION_ENDED_COPY = COPY;
export const SESSION_ENDED_FALLBACK = FALLBACK;
